/**
 * The company a role / access assignment is made in.
 *
 * The backend always records one: a blank company silently fell back to the
 * caller's company, then the user's — so the dialogs' "Platform-wide" option
 * never meant platform-wide. The dialogs now make the company explicit:
 * default it to the user's primary company, and pin it to the caller's own
 * company for tenant admins (the only company they may assign in).
 */

import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { getCompanyId } from '@lognai/plugin-sdk';

import { useUser } from '../../hooks';

export function useAssignmentCompany(
  form: UseFormReturn<{ companyId?: string } & Record<string, unknown>>,
  userId: string,
  open: boolean
) {
  const ownCompanyId = getCompanyId();
  const isPlatformAdmin = ownCompanyId == null;
  const { data: user } = useUser(open && isPlatformAdmin ? userId : undefined);
  const defaultCompanyId = isPlatformAdmin ? user?.companyId : ownCompanyId;

  useEffect(() => {
    if (open && defaultCompanyId && !form.getValues('companyId')) {
      form.setValue('companyId', defaultCompanyId);
    }
  }, [open, defaultCompanyId, form]);

  return { isPlatformAdmin };
}
